import React, { useContext, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { User, TrendingUp, MessageCircle, Calendar, Edit, Shield, Camera, Upload, FileText, Download, AlertTriangle } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import QuickNav from './QuickNav';
import SEOHead from './SEOHead';
import ProfileModal from './ProfileModal';
import NotificationSettings from './NotificationSettings';
import ChangePassword from './ChangePassword';
import TermsAndConditions from './TermsAndConditions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  // Fetch payment history
  React.useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    try {
      setLoadingPayments(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payments/my-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(response.data.payments || []);
    } catch (error) {
      console.error('Failed to fetch payment history:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const downloadInvoice = async (paymentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payments/invoice/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Download invoice error:', error);
      toast.error('Failed to download invoice');
    }
  };

  const onAvatarDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) {
      toast.error('No file selected');
      return;
    }

    // Simple validation
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPG, PNG, GIF)');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploadingAvatar(true);
    toast.info('Uploading profile picture...');
    
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      // Get token
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Please log in again');
        return;
      }

      // Make the upload request
      const response = await fetch(`${API}/profile/upload-avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const data = await response.json();

      if (data.avatar_url) {
        // Update user state with new avatar
        setUser(prev => ({
          ...prev,
          avatar_url: data.avatar_url
        }));
        
        toast.success('Profile picture updated!');
      } else {
        throw new Error('No avatar URL returned from server');
      }
    } catch (error) {
      console.error('Profile picture upload error:', error);
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const { getRootProps: getAvatarRootProps, getInputProps: getAvatarInputProps, isDragActive: isAvatarDragActive } = useDropzone({
    onDrop: onAvatarDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 1,
    multiple: false
  });

  const stats = [
    {
      label: "AI Queries Today",
      value: user?.ai_queries_today || 0,
      max: 10,
      color: "text-indigo-600",
      icon: <MessageCircle className="h-5 w-5 text-indigo-600" />
    },
    {
      label: "Days Active",
      value: user ? Math.ceil((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24)) : 0,
      color: "text-purple-600",
      icon: <Calendar className="h-5 w-5 text-purple-600" />
    },
    {
      label: "Profile Status",
      value: user?.profile?.completed_questionnaire ? "Complete" : "Incomplete",
      color: user?.profile?.completed_questionnaire ? "text-green-600" : "text-orange-600",
      icon: <Shield className="h-5 w-5" style={{ color: user?.profile?.completed_questionnaire ? "#059669" : "#ea580c" }} />
    }
  ];

  const recentActivity = [
    {
      action: "Profile completed",
      time: "Just now",
      icon: <TrendingUp className="h-4 w-4 text-green-600" />
    },
    {
      action: "Welcome to Nerd AI!",
      time: "Today",
      icon: <MessageCircle className="h-4 w-4 text-indigo-600" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 p-4 sm:p-6 lg:p-8">
      <SEOHead title="My Profile" description="Manage your Nerd Nation profile, CV, exam progress and account settings." path="/profile" noIndex={true} />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="profile-title">
                My Profile
              </h1>
              <p className="text-gray-600 text-sm">
                Manage your account and track your learning progress
              </p>
            </div>
            <Button 
              onClick={() => setShowEditModal(true)}
              className="bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900" 
              data-testid="edit-profile-button"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          </div>
        </div>

        {/* Profile Status Alert */}
        {!user?.profile?.completed_questionnaire && (
          <div className="mb-8">
            <Card className="border-orange-200 bg-orange-50" data-testid="profile-incomplete-alert">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-orange-900">Complete Your Profile</h3>
                      <p className="text-orange-700 text-sm">Add your academic details to unlock full AI advisor capabilities</p>
                    </div>
                  </div>
                  <Button 
                    className="bg-orange-600 hover:bg-orange-700"
                    onClick={() => window.location.reload()} // This will trigger profile modal
                    data-testid="complete-profile-button"
                  >
                    Complete Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <Card key={index} className="card" data-testid={`profile-stat-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {stat.icon}
                    <div>
                      <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                      <p className={`text-2xl font-bold ${stat.color}`}>
                        {stat.value}
                        {stat.max && (
                          <span className="text-sm font-normal text-gray-500">/{stat.max}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  {stat.max && (
                    <div className="w-16 h-16">
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            className="text-gray-200"
                            stroke="currentColor"
                            strokeWidth="3"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          <path
                            className="text-indigo-500"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeDasharray={`${(stat.value / stat.max) * 100}, 100`}
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Profile Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Profile Information */}
          <Card className="card" data-testid="profile-info-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5 text-indigo-600" />
                <span>Profile Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Profile Picture Section */}
                <div className="flex flex-col items-center space-y-4 pb-6 border-b border-gray-100">
                  <div className="relative">
                    {/* Avatar Display */}
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-blue-700 to-blue-800 flex items-center justify-center shadow-lg">
                      {user?.avatar_url ? (
                        <img 
                          src={user.avatar_url.startsWith('data:') ? user.avatar_url : `${BACKEND_URL}${user.avatar_url}`}
                          alt="Profile"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }} loading="lazy" decoding="async" />
                      ) : (
                        <span className="text-white text-3xl font-bold">
                          {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                    
                    {/* Upload Overlay */}
                    <div 
                      {...getAvatarRootProps()} 
                      className={`absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center cursor-pointer transition-opacity ${
                        isAvatarDragActive ? 'opacity-100' : 'opacity-0 hover:opacity-100'
                      }`}
                    >
                      <input {...getAvatarInputProps()} />
                      {uploadingAvatar ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      ) : (
                        <Camera className="h-6 w-6 text-white" />
                      )}
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Profile Picture</p>
                    <div {...getAvatarRootProps()}>
                      <input {...getAvatarInputProps()} />
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={uploadingAvatar}
                        className="text-xs"
                      >
                        <Upload className="h-3 w-3 mr-1" />
                        {uploadingAvatar ? 'Uploading...' : 'Upload Photo'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Basic Info */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">Full Name</span>
                    <span className="text-gray-900 font-semibold">{user?.full_name}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">Email</span>
                    <span className="text-gray-900">{user?.email}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">Member Since</span>
                    <span className="text-gray-900">
                      {user ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Academic Info */}
                {user?.profile?.completed_questionnaire && (
                  <div className="space-y-4 pt-4 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-900 text-lg">Academic Information</h4>
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">College</span>
                      <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                        {user.profile.college_name}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Degree</span>
                      <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-200">
                        {user.profile.degree_name}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Current Status</span>
                      <Badge variant="secondary" className="bg-orange-50 text-orange-700 border-orange-200">
                        {user.profile.study_year}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Mobile</span>
                      <span className="text-gray-900 font-mono">
                        {user.profile.mobile_number}
                      </span>
                    </div>
                    {user.profile.cv_url && (
                      <div className="flex justify-between items-center py-3">
                        <span className="text-gray-600 font-medium">CV/Resume</span>
                        <a
                          href={user.profile.cv_url.startsWith('data:') ? user.profile.cv_url : `${process.env.REACT_APP_BACKEND_URL}${user.profile.cv_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors border border-green-200"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm font-medium">Download CV</span>
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="card" data-testid="recent-activity-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <MessageCircle className="h-5 w-5 text-purple-600" />
                <span>Recent Activity</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      {activity.icon}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{activity.action}</p>
                      <p className="text-sm text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                ))}
                {recentActivity.length === 0 && (
                  <div className="text-center py-8">
                    <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No recent activity</p>
                    <p className="text-sm text-gray-400">Your activity will appear here</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enrolled Features Section */}
        {user?.enrolled_features && user.enrolled_features.length > 0 && (
          <div className="mt-8">
            <Card className="card border-2 border-purple-200">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                <CardTitle className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-purple-600" />
                  <span>My Enrolled Features & Plans</span>
                </CardTitle>
                {user?.enrollment_expiry && (
                  <p className="text-sm text-gray-600 mt-2">
                    Valid until: {new Date(user.enrollment_expiry).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  {user.enrolled_features.map((feature, index) => {
                    // Format feature name for display
                    let displayName = feature;
                    if (feature === 'all') displayName = '🎯 All Features (Complete Access)';
                    else if (feature === 'ai_advisor') displayName = '🤖 AI Career & Study Advisor';
                    else if (feature === 'learn_research') displayName = '📚 Research Paper Analysis';
                    else if (feature === 'engineering-student-success') displayName = '⚙️ Engineering Student Success Plan';
                    else if (feature === 'business-finance-student-success') displayName = '💼 Business & Finance Student Success Plan';
                    else if (feature === 'pharma-student-success') displayName = '💊 Pharma Student Success Plan';
                    else if (feature === 'lifesciences-student-success') displayName = '🧬 Life-Sciences Student Success Plan';
                    else if (feature.startsWith('exam_')) {
                      // Format exam names
                      const examName = feature.replace('exam_', '').replace('_Success_Bank', '').replace(/_/g, ' ');
                      displayName = `📝 ${examName}`;
                    }
                    
                    return (
                      <div key={index} className="border-2 border-purple-100 rounded-lg p-3 bg-gradient-to-r from-white to-purple-50 hover:shadow-md transition-shadow">
                        <p className="text-sm font-medium text-gray-800">{displayName}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> These features have been granted to your account. Some may be admin-awarded and won't appear in payment history.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Payment History */}
        {paymentHistory && paymentHistory.length > 0 && (
          <div className="mt-8">
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  <span>Payment History</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {paymentHistory.map((payment, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {payment.plan_name || 'Subscription Plan'}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Duration: {payment.plan_duration || 'N/A'}
                          </p>
                          <p className="text-sm text-gray-500">
                            Payment ID: {payment.payment_id}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(payment.completed_at).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-600 mb-2">
                            ₹{(() => {
                              if (payment.total_amount) {
                                return payment.total_amount.toFixed(2);
                              }
                              // For old records: if amount < 1000, it's likely in rupees already
                              // If amount >= 1000, it's in paise, divide by 100
                              const amount = payment.amount || 0;
                              return amount >= 1000 ? (amount / 100).toFixed(2) : amount.toFixed(2);
                            })()} INR
                          </p>
                          <Badge className={
                            payment.status === 'completed' 
                              ? "bg-green-100 text-green-800 mb-3"
                              : "bg-red-100 text-red-800 mb-3"
                          }>
                            {payment.status === 'completed' ? 'Completed' : 'Unsuccessful'}
                          </Badge>
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => downloadInvoice(payment.order_id, 'html')}
                              className="flex items-center justify-center gap-1 text-xs bg-blue-500 hover:bg-blue-700 text-white px-3 py-1.5 rounded"
                            >
                              <FileText className="h-3 w-3" />
                              HTML
                            </button>
                            <button
                              onClick={() => downloadInvoice(payment.order_id, 'pdf')}
                              className="flex items-center justify-center gap-1 text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded"
                            >
                              <FileText className="h-3 w-3" />
                              PDF
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Account Settings */}
        <div className="mt-8">
          <Card className="card" data-testid="account-settings-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-green-600" />
                <span>Account Settings</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Button 
                  variant="outline" 
                  className="justify-start" 
                  onClick={() => setShowChangePassword(true)}
                  data-testid="change-password-button"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Change Password
                </Button>
                <Button 
                  variant="outline" 
                  className="justify-start" 
                  onClick={() => setShowNotificationSettings(true)}
                  data-testid="notification-settings-button"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Notification Settings
                </Button>
                <Button 
                  variant="outline" 
                  className="justify-start" 
                  onClick={() => setShowTermsAndConditions(true)}
                  data-testid="terms-conditions-button"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Terms & Conditions
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Danger Zone - Delete Account */}
        <div className="mt-8">
          <Card className="border-red-200 bg-red-50/50">
            <CardHeader className="border-b border-red-200">
              <CardTitle className="flex items-center space-x-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                <span>Danger Zone</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Your Account</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <Button 
                    variant="outline" 
                    className="bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700" 
                    onClick={() => navigate('/delete-account')}
                    data-testid="delete-account-button"
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Delete Account
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Quick Navigation */}
        <QuickNav />
        
        {/* Edit Profile Modal */}
        {showEditModal && (
          <ProfileModal
            onComplete={async (formData) => {
              try {
                const token = localStorage.getItem('token');
                if (!token) {
                  toast.error('Please log in again');
                  return;
                }
                
                console.log('Updating profile with data:', formData);
                const response = await axios.put(`${API}/profile/me`, {
                  profile: formData
                }, {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });
                
                console.log('Profile update response:', response.data);
                setUser(response.data);
                toast.success('Profile updated successfully!');
                setShowEditModal(false);
              } catch (error) {
                console.error('Profile update error:', error);
                toast.error(error.response?.data?.detail || 'Failed to update profile');
              }
            }}
            onSkip={() => setShowEditModal(false)}
          />
        )}

        {/* Notification Settings Modal */}
        {showNotificationSettings && (
          <NotificationSettings onClose={() => setShowNotificationSettings(false)} />
        )}

        {/* Change Password Modal */}
        {showChangePassword && (
          <ChangePassword onClose={() => setShowChangePassword(false)} />
        )}

        {/* Terms & Conditions Modal */}
        {showTermsAndConditions && (
          <TermsAndConditions onClose={() => setShowTermsAndConditions(false)} />
        )}
        
        {/* Bottom spacing */}
        <div className="pb-24"></div>
      </div>
    </div>
  );
};

export default Profile;